/**
 * Delivery Details Form
 * Lives as a native block inside Dawn's product form. Handles:
 * - Live validation (shows a success banner once everything required is filled,
 *   a warning banner listing what's missing if submit is attempted early)
 * - Optional age-verification checkbox requirement
 * - Submits the form via /cart/add.js and redirects straight to /cart
 */
window.DeliveryDetailsForm = (function () {
  function isVisible(el) {
    // offsetParent is null for elements that are display:none (directly or via
    // an ancestor), which is exactly how hidden destination/card panels work here
    return el.offsetParent !== null;
  }

  function getMissingFields(wrapEl) {
    var requiredInputs = wrapEl.querySelectorAll('[data-dd-required]');
    var missing = [];
    requiredInputs.forEach(function (input) {
      if (!isVisible(input)) return;
      if (!input.value || !input.value.trim()) {
        missing.push(input);
      } else {
        input.removeAttribute('data-dd-invalid');
      }
    });
    return missing;
  }

  function isAgeCheckSatisfied(wrapEl, requireAgeCheck) {
    if (!requireAgeCheck) return true;
    var checkbox = wrapEl.querySelector('[data-dd-age-check]');
    return checkbox ? checkbox.checked : true;
  }

  function updateLiveState(wrapEl, requireAgeCheck) {
    var successEl = wrapEl.querySelector('[data-dd-success]');
    var warningEl = wrapEl.querySelector('[data-dd-warning]');
    var requiredInputs = wrapEl.querySelectorAll('[data-dd-required]');
    var allFilled = true;
    requiredInputs.forEach(function (input) {
      if (!isVisible(input)) return;
      if (!input.value || !input.value.trim()) allFilled = false;
    });
    var ageOk = isAgeCheckSatisfied(wrapEl, requireAgeCheck);

    if (allFilled && ageOk) {
      if (successEl) successEl.hidden = false;
      if (warningEl) warningEl.hidden = true;
    } else {
      if (successEl) successEl.hidden = true;
      // Only keep the warning visible if it was already showing (i.e. after a failed submit attempt)
    }
  }

  function showWarning(wrapEl, missing, ageMissing) {
    var warningEl = wrapEl.querySelector('[data-dd-warning]');
    var successEl = wrapEl.querySelector('[data-dd-success]');
    var listEl = wrapEl.querySelector('[data-dd-missing-list]');
    if (!warningEl || !listEl) return;

    if (successEl) successEl.hidden = true;

    listEl.innerHTML = '';
    missing.forEach(function (input) {
      input.setAttribute('data-dd-invalid', 'true');
      var label = wrapEl.querySelector('label[for="' + input.id + '"]');
      var li = document.createElement('li');
      li.textContent = label ? label.textContent.replace('*', '').trim() : 'Påkrævet felt';
      listEl.appendChild(li);
    });

    if (ageMissing) {
      var ageCheckbox = wrapEl.querySelector('[data-dd-age-check]');
      if (ageCheckbox) ageCheckbox.setAttribute('data-dd-invalid', 'true');
      var li = document.createElement('li');
      li.textContent = 'Bekræftelse af alder (18+)';
      listEl.appendChild(li);
    }

    warningEl.hidden = false;
    warningEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function applyCutoffHint(wrapEl, cutoffHour) {
    var dateInput = wrapEl.querySelector('[data-dd-date]');
    var hintEl = wrapEl.querySelector('[data-dd-order-hint]');
    if (!dateInput) return;

    var now = new Date();
    var pastCutoff = now.getHours() >= cutoffHour;

    var minDate = new Date(now);
    if (pastCutoff) {
      minDate.setDate(minDate.getDate() + 1);
    }
    var minDateStr = minDate.toISOString().split('T')[0];
    dateInput.setAttribute('min', minDateStr);

    // If the field already had an earlier (now-invalid) date selected, bump it forward
    if (dateInput.value && dateInput.value < minDateStr) {
      dateInput.value = minDateStr;
    }

    if (hintEl) {
      var preText = hintEl.getAttribute('data-pre-cutoff-text') || '';
      var postText = hintEl.getAttribute('data-post-cutoff-text') || '';
      var text = pastCutoff ? postText : preText;
      hintEl.textContent = text;
      hintEl.hidden = !text;
    }
  }

  function switchDestinationPanel(wrapEl, mode, destinationValues) {
    var panels = wrapEl.querySelectorAll('[data-dd-destination-panel]');
    panels.forEach(function (panel) {
      panel.hidden = panel.getAttribute('data-dd-destination-panel') !== mode;
    });

    var valueInput = wrapEl.querySelector('[data-dd-destination-value]');
    if (valueInput && destinationValues) {
      valueInput.value = destinationValues[mode] || '';
    }
  }

  function applyCardMessageVisibility(wrapEl, cardSelect) {
    var wrap = wrapEl.querySelector('[data-dd-card-message-wrap]');
    if (!wrap) return;
    var selectedOption = cardSelect.options[cardSelect.selectedIndex];
    var hide = selectedOption && selectedOption.getAttribute('data-hide-message') === 'true';
    wrap.hidden = !!hide;
  }

  function buildLineItems(wrapEl, productForm, cardMessageConfig) {
    var formData = new FormData(productForm);
    var properties = {};
    var mainVariantId = null;
    var quantity = 1;

    formData.forEach(function (value, key) {
      if (key === 'id') {
        mainVariantId = value;
      } else if (key === 'quantity') {
        quantity = value;
      } else {
        var match = key.match(/^properties\[(.+)\]$/);
        if (match) {
          properties[match[1]] = value;
        }
      }
    });

    var items = [
      {
        id: mainVariantId,
        quantity: quantity,
        properties: properties
      }
    ];

    // Ribbon add-on: a real, separate Shopify variant so its price is added to the cart total
    var ribbonSelect = wrapEl.querySelector('[data-dd-ribbon-select]');
    if (ribbonSelect && ribbonSelect.value) {
      var ribbonOption = ribbonSelect.options[ribbonSelect.selectedIndex];
      var ribbonVariantId = ribbonOption ? ribbonOption.getAttribute('data-variant-id') : '';
      if (ribbonVariantId) {
        items.push({ id: ribbonVariantId, quantity: 1 });
      }
    }

    // Card add-on: same approach
    var cardSelect = wrapEl.querySelector('[data-dd-card-select]');
    if (cardSelect && cardSelect.value) {
      var cardOption = cardSelect.options[cardSelect.selectedIndex];
      var cardVariantId = cardOption ? cardOption.getAttribute('data-variant-id') : '';
      if (cardVariantId) {
        items.push({ id: cardVariantId, quantity: 1 });
      }
    }

    // Personal card message surcharge: only charged if the customer actually
    // wrote something, and only if a real add-on variant is configured.
    // Attach the message + product name as properties on this line item too,
    // so it's obvious in the cart why the charge is there, rather than an
    // unexplained extra line.
    if (cardMessageConfig && cardMessageConfig.chargeForCardMessage && cardMessageConfig.cardMessageAddonVariantId) {
      var messageField = wrapEl.querySelector('[data-dd-card-message]');
      if (messageField && messageField.value && messageField.value.trim()) {
        var surchargeProperties = {
          'Besked': messageField.value.trim()
        };
        if (cardMessageConfig.productTitle) {
          surchargeProperties['Hører til'] = cardMessageConfig.productTitle;
        }
        items.push({
          id: cardMessageConfig.cardMessageAddonVariantId,
          quantity: 1,
          properties: surchargeProperties
        });
      }
    }

    return items;
  }

  function submitViaAjax(wrapEl, productForm, submitButton, cardMessageConfig) {
    var items = buildLineItems(wrapEl, productForm, cardMessageConfig);

    if (!items[0].id) {
      alert('Vælg venligst en variant før du fortsætter.');
      return;
    }

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw new Error(err.description || 'Der opstod en fejl. Prøv igen.');
          });
        }
        return response.json();
      })
      .then(function () {
        window.location.href = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + 'cart'
          : '/cart';
      })
      .catch(function (err) {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-busy');
        }
        alert(err.message || 'Der opstod en fejl. Prøv igen.');
      });
  }

  function init(sectionId, options) {
    options = options || {};
    var cutoffHour = typeof options.cutoffHour === 'number' ? options.cutoffHour : 14;
    var requireAgeCheck = !!options.requireAgeCheck;
    var redirectToCart = options.redirectToCart !== false;
    var destinationValues = options.destinationValues || null;
    var cardMessageConfig = {
      chargeForCardMessage: !!options.chargeForCardMessage,
      cardMessageAddonVariantId: options.cardMessageAddonVariantId || null,
      productTitle: options.productTitle || null
    };

    // Persist so the global cart-page watchdog (cart-addon-guard.js) can find
    // this even though it runs on pages where this block isn't rendered
    if (cardMessageConfig.chargeForCardMessage && cardMessageConfig.cardMessageAddonVariantId) {
      try {
        localStorage.setItem('dd_addon_config', JSON.stringify({
          variantId: cardMessageConfig.cardMessageAddonVariantId,
          enabled: true
        }));
      } catch (e) {
        // localStorage unavailable (private browsing, etc.) — watchdog simply won't run
      }
    }

    var wrapEl = document.getElementById(sectionId);
    if (!wrapEl) return;

    var productForm = null;
    if (options.productFormId) {
      productForm = document.getElementById(options.productFormId);
    }
    if (!productForm) {
      productForm = wrapEl.closest('form');
    }
    if (!productForm) return;

    applyCutoffHint(wrapEl, cutoffHour);
    updateLiveState(wrapEl, requireAgeCheck);

    wrapEl.addEventListener('click', function (e) {
      var switchBtn = e.target.closest('[data-dd-destination-switch]');
      if (!switchBtn) return;
      e.preventDefault();
      switchDestinationPanel(wrapEl, switchBtn.getAttribute('data-dd-destination-switch'), destinationValues);
      updateLiveState(wrapEl, requireAgeCheck);
    });

    wrapEl.addEventListener('input', function (e) {
      if (e.target.hasAttribute('data-dd-required') && e.target.value.trim()) {
        e.target.removeAttribute('data-dd-invalid');
      }
      updateLiveState(wrapEl, requireAgeCheck);
    });

    wrapEl.addEventListener('change', function (e) {
      if (e.target.hasAttribute('data-dd-age-check')) {
        if (e.target.checked) e.target.removeAttribute('data-dd-invalid');
        updateLiveState(wrapEl, requireAgeCheck);
      }
      if (e.target.hasAttribute('data-dd-card-select')) {
        applyCardMessageVisibility(wrapEl, e.target);
      }
    });

    var initialCardSelect = wrapEl.querySelector('[data-dd-card-select]');
    if (initialCardSelect) {
      applyCardMessageVisibility(wrapEl, initialCardSelect);
    }

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('button[type="submit"], input[type="submit"], [name="add"]');
      if (!btn) return;
      // .form resolves correctly whether the button is nested inside the form
      // or associated via a form="..." attribute elsewhere in the DOM
      if (btn.form !== productForm) return;

      // Take full control of this click no matter what Dawn (or any other script)
      // would otherwise do with it — native submit, custom fetch, anything.
      e.preventDefault();
      e.stopImmediatePropagation();

      var missing = getMissingFields(wrapEl);
      var ageOk = isAgeCheckSatisfied(wrapEl, requireAgeCheck);

      if (missing.length > 0 || !ageOk) {
        showWarning(wrapEl, missing, !ageOk);
        if (missing.length > 0) {
          missing[0].focus();
        } else {
          var ageCheckbox = wrapEl.querySelector('[data-dd-age-check]');
          if (ageCheckbox) ageCheckbox.focus();
        }
        return;
      }

      if (redirectToCart) {
        submitViaAjax(wrapEl, productForm, btn, cardMessageConfig);
      } else {
        // Let Dawn's normal flow happen, but we already swallowed this click,
        // so trigger it ourselves via requestSubmit to preserve default behavior.
        if (productForm.requestSubmit) {
          productForm.requestSubmit(btn);
        } else {
          productForm.submit();
        }
      }
    }, true); // capture phase on document — fires before any click handler on the button itself

    // Fallback: also guard native form submission in case something bypasses
    // the click handler entirely (e.g. Enter key inside a text field)
    document.addEventListener('submit', function (e) {
      if (e.target !== productForm) return;

      var missing = getMissingFields(wrapEl);
      var ageOk = isAgeCheckSatisfied(wrapEl, requireAgeCheck);

      if (missing.length > 0 || !ageOk) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showWarning(wrapEl, missing, !ageOk);
        return;
      }

      if (redirectToCart) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var submitButton = productForm.querySelector('[type="submit"], [name="add"]');
        submitViaAjax(wrapEl, productForm, submitButton, cardMessageConfig);
      }
    }, true);
  }

  return { init: init };
})();